/**
 * The running markup notes list (Phase 7) - every highlight/note/shape the reviewer has
 * placed, in the same order they'll be numbered in the flattened PDF (page, then created
 * order). Nothing here is sent anywhere; it's just what currently exists for this report -
 * edit the text or delete before "Send feedback to valuer" flattens it (see ../../api.ts).
 */
import { useEffect, useRef, useState } from 'react';
import type { AnnotationRow } from '../../reviewer/types';
import { Button } from '../ui';

const TYPE_LABEL: Record<AnnotationRow['type'], string> = {
  highlight: 'Highlight',
  note: 'Sticky note',
  shape: 'Shape',
};

interface NotesPanelProps {
  annotations: AnnotationRow[];
  numberById: Map<string, number>;
  focusId: string | null;
  onJump: (page: number) => void;
  onUpdateText: (id: string, text: string) => void;
  onDelete: (id: string) => void;
}

function NoteRow({
  annotation,
  number,
  autoFocus,
  onJump,
  onUpdateText,
  onDelete,
}: {
  annotation: AnnotationRow;
  number: number;
  autoFocus: boolean;
  onJump: (page: number) => void;
  onUpdateText: (id: string, text: string) => void;
  onDelete: (id: string) => void;
}) {
  const [text, setText] = useState(annotation.text ?? '');
  const [dirty, setDirty] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!dirty) setText(annotation.text ?? '');
  }, [annotation.text, dirty]);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  return (
    <div className="markup-note">
      <div className="markup-note__head">
        <button type="button" className="markup-note__jump" onClick={() => onJump(annotation.page)}>
          #{number} · page {annotation.page} · {TYPE_LABEL[annotation.type]}
        </button>
        <Button variant="ghost" onClick={() => onDelete(annotation.id)}>
          Delete
        </Button>
      </div>
      <textarea
        ref={textareaRef}
        className="input markup-note__text"
        placeholder="What needs to change here…"
        rows={2}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setDirty(true);
        }}
        onBlur={() => {
          if (dirty) {
            onUpdateText(annotation.id, text);
            setDirty(false);
          }
        }}
      />
    </div>
  );
}

export function NotesPanel({ annotations, numberById, focusId, onJump, onUpdateText, onDelete }: NotesPanelProps) {
  const ordered = annotations
    .slice()
    .sort((a, b) => a.page - b.page || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  if (ordered.length === 0) {
    return <p className="muted markup-notes__empty">No markup yet — use a tool above to highlight, note, or draw on the report.</p>;
  }

  return (
    <div className="markup-notes">
      {ordered.map((a) => (
        <NoteRow
          key={a.id}
          annotation={a}
          number={numberById.get(a.id) ?? 0}
          autoFocus={a.id === focusId}
          onJump={onJump}
          onUpdateText={onUpdateText}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
